import { create } from "zustand";
import ProductService from "../utils/api/products";
import NotificationService from "../utils/api/notifications";
import MarketPlaceService from "../utils/api/marketplace";
import { BASE_URL } from "../utils/constants";

const LOCAL_NOTIFICATION_PREFIX = "local-";

const getPinnedNotificationsKey = (userId) =>
  `marketplace_pinned_notifications_${userId}`;

const loadPinnedNotifications = (userId) => {
  if (!userId) return [];

  try {
    const raw = localStorage.getItem(getPinnedNotificationsKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const savePinnedNotifications = (userId, notifications) => {
  if (!userId) return;

  try {
    localStorage.setItem(
      getPinnedNotificationsKey(userId),
      JSON.stringify(notifications)
    );
  } catch (error) {
    console.error("Error saving pinned notifications:", error);
  }
};

const mergeNotifications = (serverNotifications = [], pinnedNotifications = []) => {
  const normalizedServer = Array.isArray(serverNotifications)
    ? serverNotifications
    : [];
  const normalizedPinned = Array.isArray(pinnedNotifications)
    ? pinnedNotifications
    : [];

  const byId = new Map();

  normalizedServer.forEach((notification) => {
    if (!notification?.id) return;
    byId.set(String(notification.id), notification);
  });

  normalizedPinned.forEach((notification) => {
    if (!notification?.id) return;
    byId.set(String(notification.id), notification);
  });

  return Array.from(byId.values()).sort((a, b) => {
    const aDate = new Date(a.createdAt || a.created_at || 0).getTime();
    const bDate = new Date(b.createdAt || b.created_at || 0).getTime();
    return bDate - aDate;
  });
};

const useAppStore = create((set, get) => {
  return {
    products: [],
    notifications: [],
    categories: [],
    cart: { products: [] },
    packages: [],
    user: null,
    role: null,
    initialized: false,
    loading: true,
    unreadCount: 0,
    cartCount: 0,
    lastSync: null,
    error: null,

    initialize: async (userId, userRole) => {
      const state = get();
      const currentUserId = state.user?.id != null ? String(state.user.id) : null;
      const incomingUserId = userId != null ? String(userId) : null;
      const currentRole = state.role != null ? String(state.role) : null;
      const incomingRole = userRole != null ? String(userRole) : null;

      const isSameSession =
        state.initialized &&
        incomingUserId &&
        currentUserId === incomingUserId &&
        currentRole === incomingRole;

      if (isSameSession) {
        return;
      }

      if (!incomingUserId) {
        set({
          products: [],
          notifications: [],
          categories: [],
          cart: { products: [] },
          packages: [],
          user: null,
          role: null,
          initialized: false,
          loading: false,
          unreadCount: 0,
          cartCount: 0,
          lastSync: null,
          error: null,
        });
        return;
      }

      // Reset session-scoped data before loading the next account to avoid data bleed.
      set({
        products: [],
        notifications: [],
        categories: [],
        cart: { products: [] },
        packages: [],
        user: { id: userId },
        role: userRole,
        initialized: false,
        loading: true,
        unreadCount: 0,
        cartCount: 0,
        lastSync: null,
        error: null,
      });

      try {
        const [notificationsResult, cartResult] = await Promise.allSettled([
          NotificationService.GetNotifications(userId),
          MarketPlaceService.getCartByClient(userId),
        ]);

        let notificationsData = [];
        let unreadCount = 0;

        const pinnedNotifications = loadPinnedNotifications(userId);

        if (
          notificationsResult.status === "fulfilled" &&
          notificationsResult.value?.data
        ) {
          notificationsData = notificationsResult.value.data;
          notificationsData = mergeNotifications(
            notificationsData,
            pinnedNotifications
          );
          unreadCount = notificationsData.filter((n) => !n.seen).length;
        } else {
          notificationsData = mergeNotifications([], pinnedNotifications);
          unreadCount = notificationsData.filter((n) => !n.seen).length;
          console.error(
            "Error cargando notificaciones:",
            notificationsResult.reason
          );
        }

        let cartData = { products: [] };
        let cartCount = 0;

        if (cartResult.status === "fulfilled" && cartResult.value?.data) {
          cartData = cartResult.value.data;
          cartCount =
            cartData.products?.reduce(
              (acc, item) => acc + (item.quantity || 1),
              0
            ) || 0;
          console.log(
            "Carrito cargado:",
            cartData.products?.length || 0,
            "productos, total:",
            cartCount
          );
        } else {
          console.error("Error cargando carrito:", cartResult.reason);
        }

        set({
          notifications: notificationsData,
          unreadCount: unreadCount,
          cart: cartData,
          cartCount: cartCount,
          loading: false,
        });

        const isClientOrAdmin = userRole === "4" || userRole === "1";
        const backgroundPromises = [];

        if (isClientOrAdmin) {
          backgroundPromises.push(
            fetch(`${BASE_URL}/api/get_all_products`)
              .then((res) => {
                if (!res.ok)
                  throw new Error(`HTTP error! status: ${res.status}`);
                return res.json();
              })
              .then((data) => {
                return { type: "products", data: data || [] };
              })
              .catch((err) => {
                console.error("Error productos (no crítico):", err.message);
                return { type: "products", data: [] };
              })
          );

          backgroundPromises.push(
            MarketPlaceService.getCategories()
              .then((res) => {
                const categories = res.data?.categories || [];
                return { type: "categories", data: categories };
              })
              .catch((err) => {
                return { type: "categories", data: [] };
              })
          );
        }

        if (
          ProductService.GetPackages &&
          typeof ProductService.GetPackages === "function"
        ) {
          backgroundPromises.push(
            ProductService.GetPackages()
              .then((res) => {
                const data = res.json ? res.json() : res;
                return data;
              })
              .then((data) => {
                return { type: "packages", data: data || [] };
              })
              .catch((err) => {
                return { type: "packages", data: [] };
              })
          );
        } else {
          console.log("ProductService.GetPackages no disponible - omitiendo");
        }

        if (backgroundPromises.length > 0) {
          Promise.allSettled(backgroundPromises).then((results) => {
            const updates = {
              initialized: true,
              lastSync: Date.now(),
            };

            results.forEach((result) => {
              if (result.status === "fulfilled" && result.value) {
                const { type, data } = result.value;

                switch (type) {
                  case "products":
                    updates.products = data;
                    break;
                  case "categories":
                    updates.categories = data;
                    break;
                  case "packages":
                    updates.packages = data;
                    break;
                }
              }
            });

            set(updates);
          });
        } else {
          set({ initialized: true, lastSync: Date.now() });
        }
      } catch (error) {
        console.error("Error fatal en initialize:", error);
        set({
          loading: false,
          initialized: true,
          notifications: get().notifications || [],
          unreadCount: get().unreadCount || 0,
          cart: get().cart || { products: [] },
          cartCount: get().cartCount || 0,
          error: error.message,
        });
      }
    },

    syncData: async () => {
      const { user, role, initialized } = get();

      if (!user?.id || !initialized) {
        return;
      }

      try {
        const isClientOrAdmin = role === "4" || role === "1";

        const [notificationsRes, cartRes] = await Promise.allSettled([
          NotificationService.GetNotifications(user.id),
          isClientOrAdmin
            ? MarketPlaceService.getCartByClient(user.id)
            : Promise.resolve({ data: { products: [] } }),
        ]);

        const updates = { lastSync: Date.now() };
        let changed = false;

        if (
          notificationsRes.status === "fulfilled" &&
          notificationsRes.value?.data
        ) {
          const pinnedNotifications = loadPinnedNotifications(user.id);
          const newNotifications = mergeNotifications(
            notificationsRes.value.data,
            pinnedNotifications
          );
          const newUnreadCount = newNotifications.filter((n) => !n.seen).length;

          updates.notifications = newNotifications;
          updates.unreadCount = newUnreadCount;
          changed = true;
        }

        if (cartRes.status === "fulfilled" && cartRes.value?.data) {
          const newCart = cartRes.value.data;
          const newCartCount =
            newCart.products?.reduce(
              (acc, item) => acc + (item.quantity || 1),
              0
            ) || 0;

          updates.cart = newCart;
          updates.cartCount = newCartCount;
          changed = true;

          console.log("🛒 Carrito sincronizado:", newCartCount, "productos");
        }

        if (changed) {
          set(updates);
        } else {
        }
      } catch (error) {
        console.error("Error en syncData:", error);
      }
    },

    addNotification: (notification) => {
      const userId =
        get().user?.id || JSON.parse(localStorage.getItem("user") || "null")?.id;

      set((state) => {
        const hasNotification = state.notifications.some(
          (n) => String(n.id) === String(notification.id)
        );

        const newNotifications = hasNotification
          ? state.notifications.map((n) =>
              String(n.id) === String(notification.id) ? { ...n, ...notification } : n
            )
          : [notification, ...state.notifications];

        const newUnreadCount = newNotifications.filter((n) => !n.seen).length;

        const pinnedNotifications = newNotifications.filter((n) =>
          String(n.id || "").startsWith(LOCAL_NOTIFICATION_PREFIX)
        );
        savePinnedNotifications(userId, pinnedNotifications);

        return {
          notifications: newNotifications,
          unreadCount: newUnreadCount,
        };
      });
    },

    markNotificationAsRead: (notificationId) => {
      const userId = get().user?.id;
      const isLocalNotification = String(notificationId || "").startsWith(
        LOCAL_NOTIFICATION_PREFIX
      );

      set((state) => {
        const newNotifications = state.notifications.map((n) =>
          String(n.id) === String(notificationId) ? { ...n, seen: true } : n
        );

        const newUnreadCount = newNotifications.filter((n) => !n.seen).length;

        return {
          notifications: newNotifications,
          unreadCount: newUnreadCount,
        };
      });

      const pinnedNotifications = get().notifications.filter((n) =>
        String(n.id || "").startsWith(LOCAL_NOTIFICATION_PREFIX)
      );
      savePinnedNotifications(userId, pinnedNotifications);

      if (isLocalNotification) {
        return;
      }

      NotificationService.MarkNotificationAsRead(notificationId)
        .then(() => console.log("Notificación marcada en servidor"))
        .catch((err) => console.error("Error en servidor:", err));
    },

    markAllNotificationsAsRead: () => {
      const userId = get().user?.id;
      const currentNotifications = get().notifications;
      const unreadIds = currentNotifications
        .filter((n) => !n.seen)
        .map((n) => n.id);

      set((state) => {
        const newNotifications = state.notifications.map((n) => ({
          ...n,
          seen: true,
        }));

        return {
          notifications: newNotifications,
          unreadCount: 0,
        };
      });

      const pinnedNotifications = get().notifications.filter((n) =>
        String(n.id || "").startsWith(LOCAL_NOTIFICATION_PREFIX)
      );
      savePinnedNotifications(userId, pinnedNotifications);

      const unreadServerIds = unreadIds.filter(
        (id) => !String(id || "").startsWith(LOCAL_NOTIFICATION_PREFIX)
      );

      if (unreadServerIds.length > 0) {
        NotificationService.MarkNotificationsAsRead(unreadServerIds)
          .then(() => console.log("Notificaciones marcadas en servidor"))
          .catch((err) => console.error("Error en servidor:", err));
      }
    },

    updateCart: (newCart) => {
      set((state) => ({
        cart: newCart,
        cartCount:
          newCart.products?.reduce(
            (acc, item) => acc + (item.quantity || 1),
            0
          ) || 0,
      }));
    },

    addToCart: (product) => {
      set((state) => {
        const newCart = {
          ...state.cart,
          products: [...(state.cart.products || []), product],
        };
        const newCartCount = newCart.products.reduce(
          (acc, item) => acc + (item.quantity || 1),
          0
        );

        return {
          cart: newCart,
          cartCount: newCartCount,
        };
      });
    },

    removeFromCart: (productId) => {
      set((state) => {
        const newProducts = state.cart.products.filter(
          (p) => p.id !== productId
        );
        const newCart = { ...state.cart, products: newProducts };
        const newCartCount = newProducts.reduce(
          (acc, item) => acc + (item.quantity || 1),
          0
        );

        return {
          cart: newCart,
          cartCount: newCartCount,
        };
      });
    },

    clearCart: () => {
      set({
        cart: { products: [] },
        cartCount: 0,
      });
    },

    addPackage: (newPackage) => {
      set((state) => ({
        packages: [...state.packages, newPackage],
      }));
    },

    updatePackages: (packages) => {
      set({ packages });
    },

    getProductsByCategory: (categoryId) => {
      const { products } = get();
      return products.filter((p) => p.part_category_id === categoryId);
    },

    getProductById: (productId) => {
      const { products } = get();
      return products.find((p) => p.id === productId);
    },

    getUnreadNotifications: () => {
      const { notifications } = get();
      return notifications.filter((n) => !n.seen);
    },
  };
});

export default useAppStore;
